// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { CycleTimeSegment } from '@/report/components/cycle-time-metrics';
import { formatCycleTimeDays } from '@/report/components/cycle-time-metrics';
import { Box, Divider, Stack, Tooltip, Typography, alpha } from '@mui/material';

const DOT_SIZE_PX = 8;
const TOOLTIP_DIVIDER_OPACITY = 0.3;

interface CycleTimeRowMarkersProps<K extends string> {
  segments: readonly CycleTimeSegment<K>[];
  values: Record<K, number | null>;
  singularNoun: string;
}

/**
 * A single segment-identity dot. Filled with the segment color when the
 * segment has a value; hollow when the segment has not been measured, so a
 * missing segment reads differently from a short one.
 */
function SegmentDot(props: { color: string; isPopulated: boolean }) {
  return (
    <Box
      data-testid="cycle-time-row-marker-dot"
      data-populated={props.isPopulated}
      sx={{
        width: DOT_SIZE_PX,
        height: DOT_SIZE_PX,
        borderRadius: '50%',
        flexShrink: 0,
        ...(props.isPopulated
          ? { bgcolor: props.color }
          : { border: 1, borderColor: 'text.disabled' }),
      }}
    />
  );
}

function RowMarkersTooltipContent<K extends string>(props: {
  segments: readonly CycleTimeSegment<K>[];
  values: Record<K, number | null>;
  totalDays: number | null;
}) {
  return (
    <Stack spacing={0.75} sx={{ p: 0.5 }}>
      {props.segments.map((segment) => (
        <Stack key={segment.key} spacing={0.25}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <SegmentDot
              color={segment.color}
              isPopulated={props.values[segment.key] !== null}
            />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {segment.label}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              variant="caption"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCycleTimeDays(props.values[segment.key])}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {segment.tooltip}
          </Typography>
        </Stack>
      ))}
      {props.totalDays !== null && (
        <>
          <Divider
            sx={{
              borderColor: (theme) =>
                alpha(theme.palette.common.white, TOOLTIP_DIVIDER_OPACITY),
            }}
          />
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Total
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
            >
              {formatCycleTimeDays(props.totalDays)}
            </Typography>
          </Stack>
        </>
      )}
    </Stack>
  );
}

/**
 * Compact per-row cycle-time markers: one colored pip (dot + value) per
 * segment, laid out horizontally in lifecycle order. Segment identity lives
 * on the dot color so the value text keeps full contrast; unmeasured
 * segments keep their slot as a hollow dot with an em-dash instead of a
 * fabricated zero. Negative durations are legitimate and render in
 * parentheses via formatCycleTimeDays.
 *
 * Renders nothing when no segment has been measured. Hovering the marker
 * shows each segment's label, definition, and value, plus the total when
 * all segments are populated (per-row segment values are additive, unlike
 * the report medians).
 */
export function CycleTimeRowMarkers<K extends string>(
  props: CycleTimeRowMarkersProps<K>
) {
  const populatedCount = props.segments.filter(
    (segment) => props.values[segment.key] !== null
  ).length;
  if (populatedCount === 0) {
    return null;
  }

  const totalDays =
    populatedCount === props.segments.length
      ? props.segments.reduce(
          (sum, segment) => sum + (props.values[segment.key] ?? 0),
          0
        )
      : null;

  return (
    <Tooltip
      arrow
      title={
        <RowMarkersTooltipContent
          segments={props.segments}
          values={props.values}
          totalDays={totalDays}
        />
      }
    >
      <Stack
        data-testid="cycle-time-row-markers"
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ cursor: 'help', width: 'fit-content' }}
        aria-label={`Cycle time for this ${props.singularNoun}`}
      >
        {props.segments.map((segment) => {
          const value = props.values[segment.key];
          return (
            <Stack
              key={segment.key}
              data-testid="cycle-time-row-marker"
              data-segment={segment.key}
              direction="row"
              alignItems="center"
              spacing={0.5}
            >
              <SegmentDot color={segment.color} isPopulated={value !== null} />
              <Typography
                variant="body2"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  color: value === null ? 'text.disabled' : 'text.primary',
                }}
              >
                {formatCycleTimeDays(value)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Tooltip>
  );
}
